import { onBeforeUnmount, watch, type Ref } from 'vue'

/**
 * Composable de focus trap accesible para diálogos, drawers y popovers.
 *
 * Cuando `active` es true:
 *   - Mueve el foco al primer elemento enfocable dentro de `container`.
 *   - Mantiene el foco dentro del contenedor (Tab/Shift+Tab ciclan).
 *   - Permite cerrar con Escape vía el callback `onEscape`.
 * Cuando pasa a false (o al desmontar):
 *   - Restaura el foco al elemento que lo tenía antes de abrirse.
 *
 * Uso típico en un componente con `ref` al elemento raíz del diálogo:
 *
 *   const root = ref<HTMLElement | null>(null)
 *   useFocusTrap(root, () => props.open, { onEscape: () => emit('update:open', false) })
 *
 * Solo debe haber un trap activo a la vez (un diálogo a la vez).
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface FocusTrapOptions {
  /** Llamado al pulsar Escape dentro del contenedor. */
  onEscape?: () => void
  /**
   * Si true, el foco inicial va al primer elemento; si false, no se mueve
   * automáticamente (útil para popovers que parten de una selección).
   * Por defecto true.
   */
  autofocus?: boolean
}

export function useFocusTrap(
  container: Ref<HTMLElement | null>,
  active: Ref<boolean> | (() => boolean),
  options: FocusTrapOptions = {},
) {
  const { onEscape, autofocus = true } = options
  let previouslyFocused: HTMLElement | null = null

  function getActive() {
    return typeof active === 'function' ? active() : active.value
  }

  function focusables(): HTMLElement[] {
    const root = container.value
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    )
  }

  function onKeydown(e: KeyboardEvent) {
    if (!getActive()) return
    if (e.key === 'Escape') {
      e.preventDefault()
      onEscape?.()
      return
    }
    if (e.key !== 'Tab') return

    const items = focusables()
    if (items.length === 0) {
      // Sin elementos enfocables: mantener el foco en el contenedor.
      e.preventDefault()
      container.value?.focus()
      return
    }

    const first = items[0]
    const last = items[items.length - 1]
    const activeEl = document.activeElement as HTMLElement | null

    if (e.shiftKey) {
      if (activeEl === first || !container.value?.contains(activeEl)) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (activeEl === last || !container.value?.contains(activeEl)) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  function trap() {
    const root = container.value
    if (!root) return
    // Asegura que el contenedor pueda recibir foco si está vacío.
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1')
    previouslyFocused = document.activeElement as HTMLElement | null
    if (autofocus) {
      // nextTick implícito: el DOM ya está montado cuando el watcher dispara.
      const items = focusables()
      ;(items[0] ?? root).focus()
    }
    document.addEventListener('keydown', onKeydown, true)
  }

  function release() {
    document.removeEventListener('keydown', onKeydown, true)
    // Restaurar el foco al disparador original.
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus()
      previouslyFocused = null
    }
  }

  watch(
    () => getActive(),
    (isActive, wasActive) => {
      if (isActive && !wasActive) {
        // Esperar a que el contenedor esté en el DOM (Transition incluida).
        requestAnimationFrame(() => {
          // Doble frame: el v-if puede no haber pintado todavía.
          requestAnimationFrame(trap)
        })
      } else if (!isActive && wasActive) {
        release()
      }
    },
  )

  onBeforeUnmount(release)
}
